// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// A PALETA DE COMANDOS — `Ctrl+K` (ponto 3 da proposta)
// ============================================================================
//
// 📌 **Um sítio onde se escreve em vez de se procurar.** Seis janelas na barra e 250 × 365
//    células: `234` e chega-se à viatura, `15 ago` e chega-se ao dia, `IGT` e filtra-se o cliente,
//    `pedidos` e abre-se a lista.
//
// ⚠️⚠️ **SÓ NAVEGA — não escreve nada.** Ver a nota do topo de `procura.ts`: um campo de texto que
//    aceitasse `reservar 234 15 ago` era a forma mais rápida de reservar a viatura errada.
//
// ⚠️ **A lógica de procura NÃO está aqui** — está em `procura.ts`, pura e com 19 testes. Aqui fica
//    só o que é do ecrã: o teclado, e o que cada resultado faz.
//
// ⚠️ **O ficheiro chama-se `PaletaDeComandos` e não `Paleta`** porque `paleta.ts` já é a das
//    **cores** do mapa. 📌 Duas coisas diferentes com o mesmo nome é o defeito que este projecto já
//    apanhou duas vezes (`.barra` para a barra de topo e para as da grelha; `.opcao-porque` para
//    duas explicações distintas) — e em Windows nem chegava a compilar, porque `Paleta.tsx` e
//    `paleta.ts` são o mesmo ficheiro para o sistema.
//
// ⚠️ **Com o campo vazio mostra os COMANDOS, não a frota.** Uma paleta que abre com 12 viaturas ao
//    acaso ensina que é uma lista; abrir com «Pedidos · Consultas · Hoje…» ensina o que se pode
//    escrever, que é a única coisa que um campo de texto vazio não diz por si.
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type DadosMapa, procurar, type ResultadoDaPaleta } from '@xlv-gva-mpt/dominio';
import { IconePesquisa } from './icones.js';

/** Um comando do ecrã — abrir uma janela, ir a hoje. Não é procura no plano. */
export interface ComandoDaPaleta {
  readonly chave: string;
  readonly rotulo: string;
  readonly detalhe: string;
  readonly fazer: () => void;
}

/**
 * Um verbo da **selecção** — o `Ctrl+K` contextual (§12.3 do desenho, peça H).
 *
 * ⚠️⚠️ **A paleta continua a não saber o que estes gestos fazem.** Recebe-os prontos do
 * `Mapa.tsx`, com **a mesma função** que a barra de acções recebe — duas construções do mesmo
 * gesto divergiriam, e a que divergisse era a que ninguém corre (regra 8).
 *
 * ⚠️ **Um verbo que não se aplica VEM NA MESMA, e diz porquê** — P-30 e §12.3: *«não há
 * becos»*. Medido a 2026-09-15: num dia livre, **19 dos 22 verbos** estão hoje simplesmente
 * ausentes da barra, e o sistema não tem sítio nenhum onde responder a «porque é que não
 * posso partir esta viagem?».
 */
export interface VerboDaPaleta {
  readonly chave: string;
  readonly rotulo: string;
  /** A coluna da esquerda: o âmbito quando se aplica, `não dá` quando não. */
  readonly marca: string;
  /** O que faz quando se aplica; **a razão** quando não se aplica. */
  readonly detalhe: string;
  readonly aplicavel: boolean;
  readonly fazer: () => void;
}

export interface PaletaDeComandosProps {
  readonly dados: DadosMapa;
  readonly fechar: () => void;
  /** Os comandos do ecrã, dados de fora: a paleta não sabe que janelas existem. */
  readonly comandos: readonly ComandoDaPaleta[];
  /**
   * Os verbos da selecção. **Vazio quando não há selecção** — e isso não é esconder: sem
   * célula escolhida não há sujeito nenhum de que a pergunta se faça, e a paleta volta a ser
   * exactamente o que sempre foi.
   */
  readonly contextuais?: readonly VerboDaPaleta[] | undefined;
  readonly irParaViatura: (id: string) => void;
  readonly irParaDia: (dia: number) => void;
  readonly filtrarCliente: (indice: number) => void;
  readonly filtrarMotorista: (indice: number) => void;
  readonly filtrarItinerario: (indice: number) => void;
}

/** O que fica na linha, seja um resultado do plano, um comando do ecrã ou um verbo. */
interface Linha {
  readonly chave: string;
  readonly rotulo: string;
  readonly detalhe: string | null;
  readonly marca: string;
  /** Só um verbo da selecção pode ser `false`; tudo o resto é sempre accionável. */
  readonly aplicavel: boolean;
  readonly fazer: () => void;
}

const MARCA: Record<ResultadoDaPaleta['tipo'], string> = {
  // ⚠️ «viatura + dia» — duas coisas numa linha. Ver a nota de `combinar` em `procura.ts`.
  combinado: 'ir a',
  viatura: 'viatura',
  subcontratado: 'subcontratado',
  dia: 'dia',
  cliente: 'cliente',
  motorista: 'motorista',
  itinerario: 'itinerário',
  localidade: 'local',
};

export function PaletaDeComandos({
  dados,
  fechar,
  comandos,
  contextuais,
  irParaViatura,
  irParaDia,
  filtrarCliente,
  filtrarMotorista,
  filtrarItinerario,
}: PaletaDeComandosProps) {
  const [texto, definirTexto] = useState('');
  const [activo, definirActivo] = useState(0);
  const campo = useRef<HTMLInputElement>(null);
  /**
   * O pedaço do texto que a procura NÃO reconheceu.
   *
   * ⚠️⚠️ **Diz-se em voz alta.** 📌 Foi o que faltou quando o cliente escreveu `100 BLV`: não
   * apareceu nada, e o ecrã não disse que a viatura 100 existia. Mostrar resultados de metade de um
   * pedido **sem dizer que é metade** é pior do que não mostrar nada — é a mesma disciplina do
   * arrasto, que escolhe a viatura e declara que não escolheu as datas.
   *
   * ⚠️ Uma `ref` e não estado: escrever estado dentro de um `useMemo` re-renderiza durante o
   * render. O valor lê-se no efeito que corre a seguir.
   */
  const ignoradoRef = useRef<string | null>(null);
  const [ignorado, definirIgnorado] = useState<string | null>(null);

  // ⚠️ Foco imediato: uma paleta que abre e obriga a clicar no campo não poupa tempo nenhum.
  useEffect(() => {
    campo.current?.focus();
  }, []);

  const linhas = useMemo<readonly Linha[]>(() => {
    const comoLinha = (c: ComandoDaPaleta): Linha => ({
      chave: `cmd:${c.chave}`,
      rotulo: c.rotulo,
      detalhe: c.detalhe,
      marca: 'ir',
      aplicavel: true,
      fazer: c.fazer,
    });
    const comoVerbo = (v: VerboDaPaleta): Linha => ({
      chave: `verbo:${v.chave}`,
      rotulo: v.rotulo,
      detalhe: v.detalhe,
      marca: v.marca,
      aplicavel: v.aplicavel,
      fazer: v.fazer,
    });
    /**
     * ⚠️⚠️ **A ORDEM É A DECISÃO DESTA PEÇA, e foi medida contra o que existia.**
     *
     * 📌 Antes, `Ctrl+K` com o campo vazio dava **9 linhas** — os nove comandos globais, todos
     * à vista. Num dia livre há **3** verbos aplicáveis e **19** que não se aplicam: pôr os
     * dezanove antes dos globais empurrava-os para a linha 23 de uma lista de 31, e o gesto
     * que já existe ficava pior do que estava por causa de um que é novo.
     *
     * ✅ A ordem é: **o que se pode fazer aqui · o resto do plano · as janelas · e por fim o
     * que não dá, com a razão**. Nada desaparece (P-30), o que era rápido continua rápido, e a
     * resposta a *«porquê?»* está onde se vai procurá-la — no fim, ou escrevendo o nome.
     */
    const ctx = contextuais ?? [];
    const podem = ctx.filter((v) => v.aplicavel).map(comoVerbo);
    const naoPodem = ctx.filter((v) => !v.aplicavel).map(comoVerbo);
    if (texto.trim() === '') return [...podem, ...comandos.map(comoLinha), ...naoPodem];

    const procura = procurar(dados, texto);
    ignoradoRef.current = procura.ignorado;
    const doPlano: Linha[] = procura.resultados.map((r) => ({
      chave: `${r.tipo}:${r.id ?? ''}:${r.dia ?? ''}:${r.indice ?? ''}:${r.eixo ?? ''}`,
      rotulo: r.rotulo,
      detalhe: r.detalhe,
      marca: MARCA[r.tipo],
      aplicavel: true,
      fazer: () => {
        // ⚠️⚠️ **UM RESULTADO PODE TER DUAS PARTES** — `100 01/1` leva à linha **e** à coluna.
        //    📌 Por isso são `if` independentes e não uma cadeia de `else if`: com os `else`, o
        //    resultado combinado ia à viatura e **nunca ao dia** — e parecia funcionar, porque o
        //    ecrã mexe-se. Metade de um gesto é o defeito mais difícil de ver.
        if (r.id !== undefined && r.id !== '') irParaViatura(r.id);
        if (r.dia !== undefined) irParaDia(r.dia);
        if (r.indice !== undefined && r.indice >= 0) {
          // O eixo vem escrito no combinado; nos simples deduz-se do tipo.
          const eixo =
            r.eixo ??
            (r.tipo === 'cliente'
              ? 'clientes'
              : r.tipo === 'motorista'
                ? 'motoristas'
                : r.tipo === 'itinerario'
                  ? 'rotas'
                  : null);
          if (eixo === 'clientes') filtrarCliente(r.indice);
          else if (eixo === 'motoristas') filtrarMotorista(r.indice);
          else if (eixo === 'rotas') filtrarItinerario(r.indice);
        }
        // ⚠️ `localidade` não faz nada, de propósito: o mapa não tem eixo geográfico, e o valor
        //    deste resultado é **desfazer a dúvida** de um código ambíguo (`SLM` Salamanca contra
        //    `SVQ` Sevilha, `BJZ` Badajoz contra `BIO` Bilbao — códigos reais da folha).
        //    Inventar um salto para uma linha qualquer era pior do que não saltar.
      },
    }));
    // ⚠️ Os comandos também se procuram por nome — `ped` chega aos Pedidos. Vêm **depois** dos
    //    resultados do plano porque a barra já os tem à vista: quem escreve na paleta procura uma
    //    agulha em 91 000 células, não um botão que está no topo do ecrã.
    const dosComandos = comandos
      .filter((c) => c.rotulo.toLowerCase().includes(texto.trim().toLowerCase()))
      .map(comoLinha);
    // ⚠️⚠️ **Os verbos procuram-se PELO RÓTULO, e é isto que dispensa dez atalhos** (§12.3):
    //    escreve-se `part` e aparece «Partir a viagem neste dia…». 📌 E os que **não** se
    //    aplicam continuam a aparecer na procura, no fim — é aí que a pergunta *«porque é que
    //    não posso partir isto?»* se faz, e é a única resposta que o sistema tem.
    const casa = (r: string) => r.toLowerCase().includes(texto.trim().toLowerCase());
    return [
      ...podem.filter((l) => casa(l.rotulo)),
      ...doPlano,
      ...dosComandos,
      ...naoPodem.filter((l) => casa(l.rotulo)),
    ];
  }, [
    dados,
    texto,
    comandos,
    // ⚠️⚠️ **`contextuais` TEM de ser estável entre renderizações no `Mapa.tsx`.** 📌 Um array
    //    novo a cada render dava um `linhas` novo a cada render, e o efeito abaixo repõe
    //    `activo` a 0 sempre que `linhas` muda de identidade: a seta para baixo mudava o estado,
    //    o componente redesenhava, e o cursor voltava ao topo — a paleta ficava impossível de
    //    navegar pelo teclado, sem erro nenhum a dizê-lo.
    contextuais,
    irParaViatura,
    irParaDia,
    filtrarCliente,
    filtrarMotorista,
    filtrarItinerario,
  ]);

  // ⚠️ A selecção volta ao topo a cada tecla: sem isto, escrever `23` e depois `234` deixava o
  //    cursor na terceira linha de uma lista que já é outra — e `Enter` fazia algo que ninguém viu.
  useEffect(() => {
    definirActivo(0);
    definirIgnorado(ignoradoRef.current);
  }, [texto, linhas]);

  const escolher = useCallback(
    (i: number) => {
      const l = linhas[i];
      if (l === undefined) return;
      // ⚠️⚠️ **Um verbo que não se aplica NÃO FAZ NADA e NÃO FECHA a paleta.** 📌 Fechar sem
      //    fazer é indistinguível de fazer: quem carrega em `Enter` vê a camada sair e conclui
      //    que o gesto pegou. A paleta fica aberta com a razão escrita na própria linha, que é
      //    a resposta à pergunta que levou lá.
      if (!l.aplicavel) return;
      l.fazer();
      // ⚠️⚠️ **A paleta FECHA ao escolher, e as janelas de trabalho não.** 📌 Não é incoerência: a
      //    paleta é o gesto de **chegar lá**, e depois de chegar estorva — é uma camada por cima do
      //    mapa, com o fundo escurecido. A lista de pedidos é onde se **decide**, e por isso fica.
      fechar();
    },
    [linhas, fechar],
  );

  const aoTeclar = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      definirActivo((a) => Math.min(linhas.length - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      definirActivo((a) => Math.max(0, a - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      escolher(activo);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      fechar();
    }
  };

  return (
    <>
      {/* ⚠️ O fundo fecha ao clique, e escurece: ao contrário das janelas de trabalho, esta **não**
          serve para ver o mapa por baixo — serve para sair dela o mais depressa possível. */}
      <div className="paleta-fundo" onClick={fechar} aria-hidden="true" />
      <section className="paleta-cmd" role="dialog" aria-label="Procurar no plano">
        <div className="paleta-campo">
          <IconePesquisa em={1} />
          <input
            ref={campo}
            type="text"
            value={texto}
            onChange={(e) => definirTexto(e.target.value)}
            onKeyDown={aoTeclar}
            placeholder="234 · 15 ago · IGT · pedidos"
            aria-label="procurar viatura, dia, cliente, motorista ou itinerário"
          />
        </div>
        {/* ⚠️⚠️ **O QUE FOI IGNORADO DIZ-SE, e antes da lista.** 📌 Uma lista que responde a
            metade do que se escreveu, sem dizer qual metade, leva a agir sobre a resposta errada. */}
        {ignorado === null ? null : (
          <p className="paleta-ignorado">
            ⚠️ Não reconheci <strong>{ignorado}</strong> — a mostrar o que encontrei para o resto.
          </p>
        )}
        {linhas.length === 0 ? (
          // ⚠️ Um estado vazio que **explica o que se pode escrever**, em vez de «sem resultados».
          //    📌 Numa caixa de procura, «sem resultados» só confirma o que já se vê; o que falta
          //    saber é o que ela entende.
          <p className="paleta-vazio">
            Nada com <strong>{texto}</strong>. Escreve-se um <strong>número de viatura</strong>{' '}
            (234), uma <strong>data</strong> (15 ago, 15/8), uma <strong>sigla de cliente</strong>{' '}
            (IGT), um <strong>nome</strong> de motorista ou um <strong>itinerário</strong>.
          </p>
        ) : (
          <ul className="paleta-lista" role="listbox" aria-label="resultados">
            {linhas.map((l, i) => (
              <li key={l.chave}>
                <button
                  type="button"
                  role="option"
                  aria-selected={i === activo}
                  className="paleta-linha"
                  data-activa={i === activo}
                  // ⚠️⚠️ **A identidade da linha, para a guarda a poder confrontar com a
                  //    BARRA DE ACÇÕES.** `paleta-conhece-a-seleccao.test.tsx` renderiza o mapa
                  //    em cada contexto e exige que o conjunto de `data-gesto` da barra seja
                  //    exactamente o conjunto de verbos activos aqui. 📌 Sem uma marca estável
                  //    a comparação teria de ser por rótulo, e os rótulos são diferentes dos
                  //    dois lados de propósito — na barra o âmbito é o grupo, aqui cada verbo
                  //    tem de se ler sozinho.
                  data-linha={l.chave}
                  // ⚠️⚠️ **`disabled` a sério, e não só um aspecto.** 📌 Um verbo que não se
                  //    aplica tem de ser impossível de accionar — «um botão que não faz nada e
                  //    nenhum erro a dizê-lo» é o defeito do `return` silencioso do «Resta
                  //    subcontratar», e este projecto já o pagou. A razão vai no `detalhe`,
                  //    porque um `title` tem ~1 s de atraso e ninguém espera por ele.
                  // 🅿️ **Falta a regra `.paleta-linha:disabled` no `mapa.css`** (ficheiro
                  //    fechado nesta passagem): sem ela a linha não esbate, e o sinal visível é
                  //    só a marca «não dá» e a razão. Declarado no relatório.
                  disabled={!l.aplicavel}
                  // ⚠️ `onMouseEnter` move a selecção: com o rato numa linha e o realce noutra,
                  //    `Enter` fazia uma coisa e o clique fazia outra.
                  onMouseEnter={() => definirActivo(i)}
                  onClick={() => escolher(i)}
                >
                  <span className="paleta-marca">{l.marca}</span>
                  <span className="paleta-rotulo">{l.rotulo}</span>
                  {l.detalhe === null ? null : <span className="paleta-detalhe">{l.detalhe}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className="paleta-rodape">
          ↑↓ para escolher · <kbd>Enter</kbd> para ir · <kbd>Esc</kbd> para fechar
        </p>
      </section>
    </>
  );
}
